/* eslint-disable id-length */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { type Edge, useViewport, XYPosition } from "@xyflow/react";
import { EdgeData } from "~/lib/pipeline";

type PathOptions = {
  points: XYPosition[];
};

type EdgeWithPath = Edge<EdgeData> & {
  pathOptions?: PathOptions;
};

type Bounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

const BORDER_RADIUS = 12;

// Colors from themeEdge
const COLOR_DEFAULT = "#c2cace";
const COLOR_SELECTED = "#086DD7";

// Stroke widths from themeEdge
const STROKE_WIDTH_DEFAULT = 1.5;
const STROKE_WIDTH_SELECTED = 4;

// Arrow size
const ARROW_SIZE = 8;

// Padding around viewport for edge culling (in graph coordinates)
const VIEWPORT_PADDING = 50;

// Z-index for canvas
const Z_INDEX = 1001;

/**
 * Calculate the distance between two points.
 */
function distance(a: XYPosition, b: XYPosition): number {
  return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
}

/**
 * Calculate the bounding box of an edge's points.
 */
function getEdgeBounds(points: XYPosition[]): Bounds | null {
  if (points.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return { minX, minY, maxX, maxY };
}

/**
 * Check if two bounding boxes intersect.
 */
function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/**
 * Draw a smooth bend between three points using a quadratic Bézier curve.
 */
function drawBend(
  ctx: CanvasRenderingContext2D,
  a: XYPosition,
  b: XYPosition,
  c: XYPosition,
  size: number,
): void {
  const bendSize = Math.min(distance(a, b) / 2, distance(b, c) / 2, size);
  const { x, y } = b;

  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    ctx.lineTo(x, y);
    return;
  }

  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1;
    const yDir = a.y < c.y ? 1 : -1;
    ctx.lineTo(x + bendSize * xDir, y);
    ctx.quadraticCurveTo(x, y, x, y + bendSize * yDir);
  } else {
    const xDir = a.x < c.x ? 1 : -1;
    const yDir = a.y < c.y ? -1 : 1;
    ctx.lineTo(x, y + bendSize * yDir);
    ctx.quadraticCurveTo(x, y, x + bendSize * xDir, y);
  }
}

/**
 * Draw an arrow at the end of the edge path.
 */
function drawArrow(ctx: CanvasRenderingContext2D, points: XYPosition[], color: string): void {
  if (points.length < 2) {
    return;
  }

  const lastPoint = points[points.length - 1];
  const prevPoint = points[points.length - 2];
  const angle = Math.atan2(lastPoint.y - prevPoint.y, lastPoint.x - prevPoint.x);

  ctx.save();
  ctx.translate(lastPoint.x, lastPoint.y);
  ctx.rotate(angle);

  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(-ARROW_SIZE, -ARROW_SIZE / 2);
  ctx.lineTo(0, 0);
  ctx.lineTo(-ARROW_SIZE, ARROW_SIZE / 2);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw a single edge on the canvas.
 */
function drawEdge(
  ctx: CanvasRenderingContext2D,
  edge: EdgeWithPath,
  color: string,
  strokeWidth: number,
): void {
  const points = edge.pathOptions?.points || [];

  if (points.length < 2) {
    return;
  }

  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i++) {
    if (i < points.length - 1) {
      drawBend(ctx, points[i - 1], points[i], points[i + 1], BORDER_RADIUS);
    } else {
      ctx.lineTo(points[i].x, points[i].y);
    }
  }

  ctx.stroke();
  drawArrow(ctx, points, color);
}

type EdgeCanvasProps = {
  edges: EdgeWithPath[];
};

/**
 * A canvas-based edge renderer for ReactFlow.
 *
 * Instead of rendering each edge as an individual SVG element (which can cause
 * performance issues with large graphs), this component renders all edges on a
 * single HTML canvas element positioned above group node backgrounds.
 *
 * Draws all edges on a single canvas using double-buffering:
 * - Draws to an offscreen canvas first
 * - Copies to visible canvas in one operation to prevent flicker
 * - Non-selected edges in gray, selected edges in blue (on top)
 */
export default function EdgeCanvas({ edges }: EdgeCanvasProps) {
  const { x, y, zoom } = useViewport();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Store current values in refs so draw function can access latest values
  const edgesRef = useRef(edges);
  const viewportRef = useRef({ x, y, zoom });
  edgesRef.current = edges;
  viewportRef.current = { x, y, zoom };

  // Draw function that can be called from anywhere
  const draw = useCallback((width: number, height: number) => {
    const canvas = canvasRef.current;
    const offscreenCanvas = offscreenCanvasRef.current;
    if (!canvas || !offscreenCanvas || width === 0 || height === 0) {
      return;
    }

    const ctx = canvas.getContext("2d");
    const offscreenCtx = offscreenCanvas.getContext("2d");
    if (!ctx || !offscreenCtx) {
      return;
    }

    const currentEdges = edgesRef.current;
    const { x: vx, y: vy, zoom: vz } = viewportRef.current;
    const dpr = window.devicePixelRatio || 1;

    // Calculate viewport bounds
    const viewportBounds: Bounds = {
      minX: -vx / vz - VIEWPORT_PADDING,
      minY: -vy / vz - VIEWPORT_PADDING,
      maxX: (width - vx) / vz + VIEWPORT_PADDING,
      maxY: (height - vy) / vz + VIEWPORT_PADDING,
    };

    // Clear offscreen canvas and set transform
    offscreenCtx.setTransform(1, 0, 0, 1, 0, 0);
    offscreenCtx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
    offscreenCtx.setTransform(dpr * vz, 0, 0, dpr * vz, dpr * vx, dpr * vy);

    // Separate edges into non-selected and selected
    const nonSelectedEdges: EdgeWithPath[] = [];
    const selectedEdges: EdgeWithPath[] = [];

    for (const edge of currentEdges) {
      const points = edge.pathOptions?.points;
      if (points && points.length > 0) {
        const edgeBounds = getEdgeBounds(points);
        if (edgeBounds && boundsIntersect(edgeBounds, viewportBounds)) {
          if (edge.selected) {
            selectedEdges.push(edge);
          } else {
            nonSelectedEdges.push(edge);
          }
        }
      }
    }

    // Draw non-selected edges first (gray, underneath)
    for (const edge of nonSelectedEdges) {
      drawEdge(offscreenCtx, edge, COLOR_DEFAULT, STROKE_WIDTH_DEFAULT);
    }

    // Draw selected edges on top (blue)
    for (const edge of selectedEdges) {
      drawEdge(offscreenCtx, edge, COLOR_SELECTED, STROKE_WIDTH_SELECTED);
    }

    // Copy offscreen canvas to visible canvas in one atomic operation
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = "copy";
    ctx.drawImage(offscreenCanvas, 0, 0);
    ctx.globalCompositeOperation = "source-over";
  }, []);

  // Handle canvas sizing - draw immediately after resize to prevent flicker
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = container.clientWidth;
      const height = container.clientHeight;

      // Create or resize offscreen canvas BEFORE resizing visible canvas
      if (!offscreenCanvasRef.current) {
        offscreenCanvasRef.current = document.createElement("canvas");
      }
      offscreenCanvasRef.current.width = width * dpr;
      offscreenCanvasRef.current.height = height * dpr;

      // Resize visible canvas (this clears it)
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Draw immediately to prevent flash
      draw(width, height);

      setCanvasSize({ width, height });
    };

    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();

    // Listen for devicePixelRatio changes (e.g., moving window between monitors
    // with different pixel densities). This ensures the canvas re-scales correctly.
    // We re-register the media query each time because once the DPR changes, the
    // previous query no longer matches.
    let dprMediaQuery: MediaQueryList | null = null;
    let dprHandler: (() => void) | null = null;

    const listenForDprChange = () => {
      dprMediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      dprHandler = () => {
        resize();
        listenForDprChange();
      };
      dprMediaQuery.addEventListener("change", dprHandler, { once: true });
    };
    listenForDprChange();

    return () => {
      observer.disconnect();
      if (dprMediaQuery && dprHandler) {
        dprMediaQuery.removeEventListener("change", dprHandler);
      }
    };
  }, [draw]);

  // Redraw when edges or viewport change
  useLayoutEffect(() => {
    draw(canvasSize.width, canvasSize.height);
  }, [edges, x, y, zoom, canvasSize, draw]);

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      <canvas ref={canvasRef} className="absolute inset-0" style={{ zIndex: Z_INDEX }} />
    </div>
  );
}
